'use client';
import { useCallback, useEffect, useRef } from 'react';
import { FBT_LIST_NAME } from '../../../constants/constants';
import { useFbtContext } from '../fbt-context/fbt-context-provider';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';
import { useBreakpoint } from '@cat-ecom/pcc-components';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { CurrentItems } from 'services/fbtItems/types';
import { ProductPriceAvailabilityHeadless } from 'services/priceAvailability/types';
import { getItemByPartNumber } from '../utils';

interface FbtGaEventProps {
  storeLocationId: string;
  currentProduct: CurrentItems;
  isCatCorp: boolean;
}

const FbtGaEvent: React.FC<FbtGaEventProps> = ({
  storeLocationId,
  currentProduct,
  isCatCorp
}) => {
  const { currency, cartDetails, fbtProductsWithPrice, itemSummary } =
    useFbtContext();
  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();
  const hasViewListItem = useRef(false);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const mergeFbtCurrentItems = useCallback(
    (productsWithPrice: ProductPriceAvailabilityHeadless[]) => {
      if (isCatCorp || !productsWithPrice || !itemSummary?.products) {
        return [];
      }
      return productsWithPrice.map((item) => {
        const summaryItem = getItemByPartNumber(
          item.partNumber,
          itemSummary?.products
        );
        return {
          ...item,
          ...summaryItem,
          manufacturer: summaryItem?.manufacturer || 'Cat',
          unformattedUnitPrice: item?.price?.unformatted?.totalPrice,
          price: item?.price?.formatted?.totalPrice
        };
      });
    },
    [itemSummary, isCatCorp]
  );

  useEffect(() => {
    const fbtViewItemList = {
      item_list_id: FBT_LIST_NAME,
      item_list_name: FBT_LIST_NAME,
      currency: currency
    };
    const getUpdatedCurrentItem = (
      currentItemPriceData: ProductPriceAvailabilityHeadless
    ) => {
      return {
        ...currentItemPriceData,
        unformattedUnitPrice:
          currentItemPriceData?.price?.unformatted?.totalPrice,
        price: currentItemPriceData?.price?.formatted?.totalPrice
      };
    };
    const currentItemWithPrice = getItemByPartNumber(
      currentProduct.partNumber,
      fbtProductsWithPrice
    );
    const updatedCurrentItem = getUpdatedCurrentItem(currentItemWithPrice);

    const mergedFbtResponse = [
      updatedCurrentItem,
      ...mergeFbtCurrentItems(fbtProductsWithPrice)
    ];

    const viewReplacePayload = {
      products: mergedFbtResponse,
      locationId: storeLocationId,
      itemListData: fbtViewItemList,
      cartItemsData: cartDetails,
      cartId: cartDetails?.orderHeader?.orderId,
      isProductsData: true
    };

    const condition =
      fbtProductsWithPrice?.length > 0 && itemSummary?.products?.length > 0;

    if (
      isMD &&
      mergedFbtResponse?.length > 0 &&
      !hasViewListItem.current &&
      condition
    ) {
      fireViewItemListEvent(viewReplacePayload);
      hasViewListItem.current = true;
    }
  }, [
    cartDetails,
    storeLocationId,
    fireViewItemListEvent,
    isCatCorp,
    isMD,
    currency,
    mergeFbtCurrentItems,
    currentProduct,
    itemSummary,
    fbtProductsWithPrice
  ]);

  return null;
};

export default FbtGaEvent;
